import Sticky from "react-stickynode"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import dynamic from "next/dynamic"
import MetaData from "@/common/components/MetaData"
import SectionLine from "@/common/components/_UI/SectionLine"

import ComplianceProDark from "@/common/assets/image/products/icons/ComplianceProDark.svg"
import AuditTrail from "@/common/assets/image/products/compliance/pro/AuditTrail.webp"
import IDV from "@/common/assets/image/products/compliance/pro/IDV.webp"
import CDD from "@/common/assets/image/products/compliance/pro/CDD.png"
import FATF from "@/common/assets/image/products/compliance/pro/FATF.png"
import APG from "@/common/assets/image/products/compliance/pro/APG.png"
import OFAC from "@/common/assets/image/products/compliance/pro/OFAC.png"
import HMTreasury from "@/common/assets/image/products/compliance/pro/HMTreasury.png"

const NavbarNew = dynamic(import("@/containers/common/NavbarNew"))
const ComplianceFlow = dynamic(
  import("@/containers/compliance/complianceFlow/ComplianceFlow"),
  { ssr: false }
)

import VideoSection from "@/common/components/VideoSection"
import Statistic from "@/common/components/Statistic"
import TrustedBy from "@/common/components/TrustedBy"
import { DarkBGWrapper, LayoutWrapper } from "@/common/components/LayoutStyle"
import Description from "@/common/components/Description"
import ProductCTA from "@/common/components/ProductCTA"
import ProductViewMore from "@/common/components/ProductViewMore"
import { ProductBanner } from "@/common/components/ProductBanner"
import { ProductNames, SolutionNames } from "@/common/data/Products"
import Footer from "@/containers/common/FooterNew"
import { useRouter } from "next/router"
import NextImg from "@/common/components/NextImg"
import KYAIcon from "@/common/assets/image/products/compliance/pro/kya_icon.png"
import KYBIcon from "@/common/assets/image/products/compliance/pro/kyb_icon.png"
import KYCIcon from "@/common/assets/image/products/compliance/pro/kyc_icon.png"
import { CustomerCard } from "@/common/components/SuccessStory"
import { theme } from "@/common/Mixin"
import styled from "styled-components"

const ComplianceProPage = () => {
  const { t } = useTranslation(["compliancePro"])
  const { t: commonT } = useTranslation(["common"])
  const { t: compT } = useTranslation(["compliance"])
  const { locale } = useRouter()

  return (
    <>
      <MetaData
        title={t("meta.title")}
        description={t("meta.description")}
        url={t("meta.url")}
        image="https://storage.googleapis.com/kryptogo-official-website-public/og-logo.webp"
        languageAlternates={[
          {
            hrefLang: "en-US",
            href: "https://www.kryptogo.com/products/compliance-pro"
          },
          {
            hrefLang: "zh-TW",
            href: "https://www.kryptogo.com/tw/products/compliance-pro"
          },
          {
            hrefLang: "zh-CN",
            href: "https://www.kryptogo.com/cn/products/compliance-pro"
          },
          {
            hrefLang: "ja-JP",
            href: "https://www.kryptogo.com/ja/products/compliance-pro"
          }
        ]}
      />

      <Sticky top={0} innerZ={1000} activeClass="sticky-active">
        <NavbarNew />
      </Sticky>
      <DarkBGWrapper></DarkBGWrapper>
      <LayoutWrapper>
        <ProductBanner
          icon={ComplianceProDark.src}
          bannerType={ProductNames.CompliancePro}
          title={t("Banner.productName")}
          titleh1={t("Banner.title")}
          description={t("Banner.description")}
          primaryButton={{
            content: t("Banner.button1"),
            href: "https://app.kryptogo.com/compliance/signup"
          }}
          subtleButton={{
            content: t("Banner.button2"),
            href: "/contact-us"
          }}
        />
        <SectionLine />
        <VideoSection videoUrl="https://www.youtube.com/embed/CH_62I0CrC4" />
        <div
          style={{ display: "flex", flexDirection: "column", gap: "60px" }}
          id="kyabc"
        >
          <Description
            id="kyabc-desc"
            backgroundType="none"
            items={[
              {
                wide: true,
                titles: [
                  { text: t("kyabc.title.1"), textClass: "primary title" },
                  { text: t("kyabc.title.2"), textType: "h2" },
                  { text: t("kyabc.desc"), textClass: "body disabled" }
                ]
              }
            ]}
          />
          <KYABCCardWrapper id="kyabc-card-wrapper" className="kg-wrapper">
            <CustomerCard
              style={{ height: "100%" }}
              company={t("kyabc.card.1.company")}
              name={t("kyabc.card.1.name")}
              description={t("kyabc.card.1.desc")}
              image={KYAIcon}
            />
            <CustomerCard
              style={{ height: "100%" }}
              company={t("kyabc.card.2.company")}
              name={t("kyabc.card.2.name")}
              description={t("kyabc.card.2.desc")}
              image={KYBIcon}
            />
            <CustomerCard
              style={{ height: "100%" }}
              company={t("kyabc.card.3.company")}
              name={t("kyabc.card.3.name")}
              description={t("kyabc.card.3.desc")}
              image={KYCIcon}
            />
          </KYABCCardWrapper>
        </div>
        <Statistic
          title={t("Statistic.title")}
          items={[
            {
              number: 10,
              numberSuffix: t("Statistic.items.0.numberSuffix"),
              description: t("Statistic.items.0.description"),
              trend: "smaller"
            },
            {
              number: 200,
              numberSuffix: t("Statistic.items.1.numberSuffix"),
              description: t("Statistic.items.1.description"),
              trend: "bigger"
            },
            {
              number: 83.2,
              numberSuffix: t("Statistic.items.2.numberSuffix"),
              description: t("Statistic.items.2.description"),
              trend: "bigger"
            },
            {
              number: 10,
              numberSuffix: t("Statistic.items.3.numberSuffix"),
              description: t("Statistic.items.3.description"),
              trend: "bigger"
            }
          ]}
          buttonText={t("Statistic.button")}
          buttonHref="https://app.kryptogo.com/compliance/signup"
        />
        <Description
          backgroundType="none"
          items={[
            {
              wide: true,
              titles: [
                {
                  text: t("kycBanner.productName"),
                  textClass: "primary title"
                },
                { text: t("kycBanner.title"), textType: "h2" },
                { text: t("kycBanner.description"), textClass: "body disabled" }
              ]
            }
          ]}
        />
        <ProductBanner
          short
          paddingTop="0"
          title={t("swiper.items.0.productName")}
          titleType={"primary"}
          titleh3={t("swiper.items.0.title")}
          titleh3Type={"normal"}
          description={t("swiper.items.0.description")}
          comp2={<NextImg src={IDV.src} />}
          checkList={[
            {
              listColor: "disabled",
              title: t("swiper.items.0.checkList.0.title"),
              list: [
                { description: t("swiper.items.0.checkList.0.list.0") },
                { description: t("swiper.items.0.checkList.0.list.1") }
              ]
            },
            {
              listColor: "disabled",
              title: t("swiper.items.0.checkList.1.title"),
              list: [
                { description: t("swiper.items.0.checkList.1.list.0") },
                { description: t("swiper.items.0.checkList.1.list.1") }
              ]
            }
          ]}
        />
        <ProductBanner
          short
          paddingTop="0"
          title={t("swiper.items.1.productName")}
          titleType={"primary"}
          titleh3={t("swiper.items.1.title")}
          titleh3Type={"normal"}
          description={t("swiper.items.1.description")}
          comp2={<NextImg src={CDD.src} />}
          checkList={[
            {
              title: t("swiper.items.1.checkList.0.title"),
              listColor: "disabled",
              list: [
                { description: t("swiper.items.1.checkList.0.list.0") },
                { description: t("swiper.items.1.checkList.0.list.1") },
                { description: t("swiper.items.1.checkList.0.list.2") },
                { description: t("swiper.items.1.checkList.0.list.3") },
                { description: t("swiper.items.1.checkList.0.list.4") },
                { description: t("swiper.items.1.checkList.0.list.5") }
              ]
            },
            {
              title: t("swiper.items.1.checkList.1.title"),
              listColor: "disabled",
              list: [
                { description: t("swiper.items.1.checkList.1.list.0") },
                { description: t("swiper.items.1.checkList.1.list.1") },
                { description: t("swiper.items.1.checkList.1.list.2") }
              ]
            }
          ]}
        />
        <ProductBanner
          short
          paddingTop="0"
          title={t("swiper.items.2.productName")}
          titleType={"primary"}
          titleh3={t("swiper.items.2.title")}
          titleh3Type={"normal"}
          description={t("swiper.items.2.description")}
          comp2={<NextImg src={AuditTrail.src} />}
          checkList={[
            {
              listColor: "disabled",
              list: [
                { description: t("swiper.items.2.checkList.0.list.0") },
                { description: t("swiper.items.2.checkList.0.list.1") },
                { description: t("swiper.items.2.checkList.0.list.2") },
                { description: t("swiper.items.2.checkList.0.list.3") },
                { description: t("swiper.items.2.checkList.0.list.4") }
              ]
            }
          ]}
        />
        <Description
          items={[
            {
              titles: [{ text: t("guidelines.title"), textType: "h3" }],
              subDescriptions: [
                {
                  icon: FATF,
                  content: t("guidelines.0.description")
                },
                {
                  icon: APG,
                  content: t("guidelines.1.description")
                },
                {
                  icon: OFAC,
                  content: t("guidelines.2.description")
                },
                {
                  icon: HMTreasury,
                  content: t("guidelines.3.description")
                }
              ],
              wide: true
            }
          ]}
        />
        <ComplianceFlow t={compT} locale={locale} />
        <TrustedBy title={t("trustedBy.title")} />
        <ProductCTA
          title={t("CTA.title")}
          desc={t("CTA.desc")}
          primaryButton={{ text: t("CTA.button1") }}
          secondaryButton={{
            text: t("CTA.button2"),
            href: "https://app.kryptogo.com/compliance/signup"
          }}
        />
        <ProductViewMore
          commonT={commonT}
          title={commonT("ViewMore.title")}
          tab={[
            {
              name: commonT("ViewMore.products"),
              content: [
                ProductNames.ComplyFlow,
                ProductNames.ComplianceLite,
                ProductNames.ComplianceAPI
              ]
            },
            {
              name: commonT("ViewMore.solutions"),
              content: [SolutionNames.Defi]
            }
          ]}
          actionText={commonT("ViewMore.allProducts")}
        ></ProductViewMore>
        <Footer />
      </LayoutWrapper>
    </>
  )
}

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, [
      "common",
      "contactUs",
      "compliance",
      "compliancePro"
    ]))
  }
})

export default ComplianceProPage

const KYABCCardWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-self: stretch;
  align-items: center;
  justify-content: center;

  @media (${theme.lg}) {
    flex-direction: column;
  }
`
